import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import AvisoActivarNotificaciones from "./AvisoActivarNotificaciones";
import Permisos from "./Permisos";

// Campana con los avisos de cambios en MIS pisos (asistenta / supervisora /
// comprador). Cada aviso hay que marcarlo con OK, uno a uno. El contador se
// refresca al abrir la app, al volver a ella y cada 90 s. Si se entra desde el
// aviso del móvil (?notificaciones=1) se abre sola. Debajo, el historial de todos
// los avisos ya vistos.

const DIAS = [["l", "Lunes"], ["m", "Martes"], ["x", "Miércoles"], ["j", "Jueves"], ["v", "Viernes"], ["s", "Sábado"]];
const franjaDia = (h, d) => (h?.[`${d}_e`] || h?.[`${d}_s`] ? `${h[`${d}_e`] || "--"} a ${h[`${d}_s`] || "--"}` : null);
const fmtFecha = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)} a las ${s.slice(11, 16)}` : "");

// Fila "Antes → Ahora" bien visible
const AntesAhora = ({ etiqueta, antes, ahora }) => (
  <div style={{ display: "grid", gridTemplateColumns: etiqueta ? "88px 1fr auto 1fr" : "1fr auto 1fr", gap: 8, alignItems: "center", marginTop: 6 }}>
    {etiqueta && <div style={{ fontWeight: 800, color: "#334155", fontSize: 14 }}>{etiqueta}</div>}
    <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 10, padding: "6px 8px", fontSize: 14, textAlign: "center" }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.5, opacity: 0.8 }}>ANTES</div>
      <div style={{ textDecoration: "line-through", fontWeight: 600 }}>{antes || "—"}</div>
    </div>
    <div style={{ fontSize: 20, color: "#64748b", fontWeight: 900 }}>➜</div>
    <div style={{ background: "#dcfce7", color: "#14532d", borderRadius: 10, padding: "6px 8px", fontSize: 14.5, textAlign: "center", border: "2px solid #86efac" }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.5, opacity: 0.8 }}>AHORA</div>
      <div style={{ fontWeight: 900 }}>{ahora || "—"}</div>
    </div>
  </div>
);

function contenido(n, rol) {
  const a = n.valor_conocido || {}, b = n.valor_actual || {};
  const piso = n.abreviatura || `piso ${n.id_piso}`;
  switch (n.tema) {
    case "piso":
      return b.tiene
        ? { icono: "🏠", titulo: "Tienes un piso nuevo", color: "#16a34a", cuerpo: <div style={{ fontSize: 20, fontWeight: 900, color: "#14532d", marginTop: 4 }}>{piso}</div> }
        : { icono: "🚪", titulo: "Ya no tienes este piso", color: "#dc2626", cuerpo: <div style={{ fontSize: 20, fontWeight: 900, color: "#991b1b", marginTop: 4, textDecoration: "line-through" }}>{piso}</div> };
    case "horario": {
      const cambian = DIAS.filter(([d]) => franjaDia(a, d) !== franjaDia(b, d));
      return {
        icono: "🕐", titulo: `${rol === "supervisora" ? "Cambia el horario de la asistenta" : "Cambia tu horario"} · ${piso}`, color: "#d97706",
        cuerpo: (
          <>
            {cambian.map(([d, nombre]) => <AntesAhora key={d} etiqueta={nombre} antes={franjaDia(a, d) || "sin servicio"} ahora={franjaDia(b, d) || "sin servicio"} />)}
            <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 6 }}>Los demás días no cambian.</div>
          </>
        ),
      };
    }
    case "duracion":
      return {
        icono: "⏱️", titulo: `Cambia la duración del servicio · ${piso}`, color: "#d97706",
        cuerpo: (
          <>
            {a.horas_dia !== b.horas_dia && <AntesAhora etiqueta="Al día" antes={a.horas_dia ? `${a.horas_dia} h` : null} ahora={b.horas_dia ? `${b.horas_dia} h` : null} />}
            {a.horas_semana !== b.horas_semana && <AntesAhora etiqueta="A la semana" antes={a.horas_semana ? `${a.horas_semana} h` : null} ahora={b.horas_semana ? `${b.horas_semana} h` : null} />}
          </>
        ),
      };
    case "asistenta":
      return { icono: "👤", titulo: `Cambia la asistenta · ${piso}`, color: "#7c3aed", cuerpo: <AntesAhora antes={a.nombre || "sin asistenta"} ahora={b.nombre || "sin asistenta"} /> };
    case "supervisora":
      return { icono: "👩‍💼", titulo: `Cambia tu supervisora · ${piso}`, color: "#7c3aed", cuerpo: <AntesAhora antes={a.nombre || a.user || "sin supervisora"} ahora={b.nombre || b.user || "sin supervisora"} /> };
    case "ruta":
      return { icono: "🗺️", titulo: `Cambia la ruta · ${piso}`, color: "#2563eb", cuerpo: <AntesAhora antes={a.ruta != null ? `Ruta ${a.ruta}` : null} ahora={b.ruta != null ? `Ruta ${b.ruta}` : null} /> };
    case "franja":
      return { icono: "🚚", titulo: `Cambia la franja de entrega · ${piso}`, color: "#2563eb", cuerpo: <AntesAhora antes={a.franja || "sin franja"} ahora={b.franja || "sin franja"} /> };
    case "sustitucion": {
      const dm = (x) => (x ? `${x.slice(8, 10)}/${x.slice(5, 7)}` : null);
      const periodo = (v) => (v.hasta ? (v.hasta === v.desde ? `El ${dm(v.desde)}` : `Del ${dm(v.desde)} al ${dm(v.hasta)}`) : `Desde el ${dm(v.desde)}`);
      const horario = (v) => (v.hora_entrada || v.hora_salida ? `${v.hora_entrada || "--"} a ${v.hora_salida || "--"}` : null);
      const quien = (v) => (rol === "asistenta" ? "Tienes una sustitución" : `${v.sustituta || "Sustituta"} sustituye a ${v.titular || "la titular"}`);
      if (b.anulada) {
        return {
          icono: "❌", titulo: `Sustitución anulada · ${piso}`, color: "#dc2626",
          cuerpo: <div style={{ fontSize: 15, color: "#991b1b", marginTop: 6, textDecoration: "line-through" }}>{quien(b)} · {periodo(b)}</div>,
        };
      }
      const cambios = n.valor_conocido ? [
        ["Quién", quien(a), quien(b)],
        ["Fechas", periodo(a), periodo(b)],
        ["Horario", horario(a), horario(b)],
      ].filter(([, x, y]) => x !== y) : [];
      return {
        icono: "🔁", titulo: `${cambios.length ? "Cambia la sustitución" : "Sustitución"} · ${piso}`, color: "#0891b2",
        cuerpo: cambios.length ? (
          <>{cambios.map(([e, x, y]) => <AntesAhora key={e} etiqueta={e} antes={x} ahora={y} />)}</>
        ) : (
          <div style={{ background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 10, padding: "8px 10px", marginTop: 6 }}>
            <div style={{ fontWeight: 900, color: "#155e75", fontSize: 15.5 }}>{quien(b)}</div>
            <div style={{ color: "#0e7490", fontSize: 14, marginTop: 2 }}>
              📅 {periodo(b)}{horario(b) ? ` · 🕐 ${horario(b)}` : ""}
            </div>
          </div>
        ),
      };
    }
    default:
      return { icono: "🔔", titulo: piso, color: "#64748b", cuerpo: null };
  }
}

const Tarjeta = ({ x, rol, onOk, vista }) => {
  const c = contenido(x, rol);
  return (
    <div style={{
      border: `2px solid ${vista ? "#e2e8f0" : c.color}`, borderLeftWidth: 7, background: vista ? "#f8fafc" : "#fff",
      borderRadius: 14, padding: "12px 14px", marginBottom: 10, opacity: vista ? 0.85 : 1,
    }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <span style={{ fontSize: 26 }}>{c.icono}</span>
        <div style={{ flex: 1, minWidth: 0, fontWeight: 900, color: "#1e293b", fontSize: 15.5 }}>{c.titulo}</div>
      </div>
      {c.cuerpo}
      <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 8 }}>
        Cambio del {fmtFecha(x.actualizada)}{vista && x.ok_at ? ` · visto el ${fmtFecha(x.ok_at)}` : ""}
      </div>
      {!vista && (
        <button onClick={() => onOk(x.id)} style={{
          marginTop: 10, width: "100%", border: "none", borderRadius: 12, padding: "12px 0", background: "#16a34a", color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer",
        }}>✓ Entendido</button>
      )}
    </div>
  );
};

// Agrupa los avisos por el día del cambio (más reciente primero), con el día de título.
const NOMBRE_DIA = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const NOMBRE_MES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const claveDia = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const tituloDia = (clave) => {
  const [y, m, d] = clave.split("-").map(Number);
  const f = new Date(y, m - 1, d);
  const hoy = new Date();
  const ayer = new Date(); ayer.setDate(hoy.getDate() - 1);
  const texto = `${NOMBRE_DIA[f.getDay()]} ${d} de ${NOMBRE_MES[m - 1]}${y !== hoy.getFullYear() ? ` de ${y}` : ""}`;
  if (clave === claveDia(hoy)) return `Hoy · ${texto}`;
  if (clave === claveDia(ayer)) return `Ayer · ${texto}`;
  return texto;
};
const PorDia = ({ lista, render }) => {
  const grupos = {};
  [...lista].sort((a, b) => String(b.actualizada || "").localeCompare(String(a.actualizada || ""))).forEach((x) => {
    const k = (x.actualizada || "").slice(0, 10) || "sin fecha";
    (grupos[k] = grupos[k] || []).push(x);
  });
  return Object.keys(grupos).sort().reverse().map((k) => (
    <div key={k}>
      <div style={{
        fontSize: 13.5, fontWeight: 900, color: "#475569", textTransform: "uppercase", letterSpacing: 0.3,
        margin: "12px 2px 6px", paddingBottom: 4, borderBottom: "2px solid #e2e8f0",
      }}>📅 {k === "sin fecha" ? "Sin fecha" : tituloDia(k)}</div>
      {grupos[k].map(render)}
    </div>
  ));
};

export default function CampanaNotificaciones({ rol, estiloBoton, usuario, conActivarPush = false, conPermisos = false }) {
  const [n, setN] = useState(0);
  const [abierto, setAbierto] = useState(false);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  const [verHistorial, setVerHistorial] = useState(false);

  const contar = useCallback(() => {
    axios.get("/api/notificaciones/contador").then((r) => setN(r.data?.pendientes || 0)).catch(() => {});
  }, []);
  const cargar = useCallback(() => {
    setError("");
    axios.get("/api/notificaciones/").then((r) => { setDatos(r.data); setN((r.data?.pendientes || []).length); })
      .catch(() => setError("No se pudieron cargar los avisos."));
  }, []);

  useEffect(() => {
    contar();
    const t = setInterval(contar, 90000);
    const alVolver = () => { if (document.visibilityState === "visible") contar(); };
    document.addEventListener("visibilitychange", alVolver);
    if (new URLSearchParams(window.location.search).get("notificaciones") === "1") { setAbierto(true); cargar(); }
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", alVolver); };
  }, [contar, cargar]);

  const abrir = () => { setAbierto(true); cargar(); };
  const ok = (id) => {
    setDatos((d) => {
      if (!d) return d;
      const x = d.pendientes.find((p) => p.id === id);
      return { pendientes: d.pendientes.filter((p) => p.id !== id), leidas: x ? [{ ...x, ok_at: new Date().toISOString().slice(0, 19) }, ...d.leidas] : d.leidas };
    });
    setN((v) => Math.max(0, v - 1));
    axios.post(`/api/notificaciones/${id}/ok`).catch(() => contar());
  };

  const pendientes = datos?.pendientes || [];
  const leidas = datos?.leidas || [];
  return (
    <>
      <button onClick={abrir} title={n ? `${n} aviso${n !== 1 ? "s" : ""} por ver` : "Avisos de cambios en tus pisos"} style={{
        display: "inline-flex", alignItems: "center", gap: 6, border: "none", borderRadius: 999, padding: n ? "8px 14px 8px 10px" : "8px 12px",
        cursor: "pointer", fontSize: 18, fontWeight: 900, background: "#eef2ff", color: "#1e3a5f", boxShadow: "none", ...estiloBoton,
        // con avisos resalta en amarillo; sin avisos, verde (todo visto)
        ...(n ? { background: "#fef3c7", boxShadow: "0 0 0 2px #f59e0b" } : { background: "#dcfce7", boxShadow: "0 0 0 2px #22c55e" }),
      }}>
        <span>🔔</span>
        {n > 0 && (
          <span style={{ background: "#dc2626", color: "#fff", borderRadius: 999, padding: "2px 9px", fontSize: 13.5, fontWeight: 900, whiteSpace: "nowrap" }}>
            {n > 99 ? "99+" : n} por ver
          </span>
        )}
      </button>

      {abierto && (
        <div onClick={() => setAbierto(false)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 4000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "30px 10px", overflowY: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 18, width: "min(540px, 100%)", padding: "16px 14px 12px", boxShadow: "0 20px 50px rgba(0,0,0,0.3)", textAlign: "left" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div style={{ flex: 1, fontSize: 19, fontWeight: 900, color: "#1e3a5f" }}>🔔 Cambios en tus pisos</div>
              <button onClick={() => setAbierto(false)} style={{ border: "none", background: "#f1f5f9", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: 800 }}>✕</button>
            </div>
            {datos && (
              <div style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 12, color: pendientes.length ? "#b45309" : "#15803d" }}>
                {pendientes.length ? `Te quedan ${pendientes.length} aviso${pendientes.length !== 1 ? "s" : ""} por ver. Marca «Entendido» en cada uno.` : "✅ Ya has visto todos los avisos."}
              </div>
            )}
            {conActivarPush && usuario && (conPermisos ? <Permisos user={usuario} /> : <AvisoActivarNotificaciones user={usuario} compacto />)}
            {error && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{error}</div>}
            {!datos && !error && <div style={{ color: "#94a3b8", padding: 20, textAlign: "center" }}>Cargando…</div>}
            <PorDia lista={pendientes} render={(x) => <Tarjeta key={x.id} x={x} rol={rol} onOk={ok} />} />

            {leidas.length > 0 && (
              <div style={{ marginTop: 8, borderTop: "1px solid #e2e8f0", paddingTop: 10 }}>
                <button onClick={() => setVerHistorial((v) => !v)} style={{
                  width: "100%", border: "2px solid #c7d2fe", borderRadius: 12, padding: "9px 12px", background: "#eef2ff", color: "#3730a3",
                  fontWeight: 800, fontSize: 14, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <span>📜 Todos los cambios que ya has visto ({leidas.length})</span>
                  <span style={{ transition: "transform .2s", transform: verHistorial ? "rotate(180deg)" : "none" }}>▾</span>
                </button>
                {verHistorial && <div style={{ marginTop: 10 }}><PorDia lista={leidas} render={(x) => <Tarjeta key={x.id} x={x} rol={rol} vista />} /></div>}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
